import React, { useState, useEffect, useContext, useMemo } from 'react';
import {
  UserGroupIcon,
  UserIcon,
  ShieldCheckIcon,
  AcademicCapIcon,
  UserMinusIcon,
  ChevronDownIcon,
  ChevronRightIcon,
  BuildingOfficeIcon,
  TrophyIcon,
  StarIcon,
  SparklesIcon,
} from '@heroicons/react/24/outline';
import { AuthContext } from '../App';
import apiClient from '../utils/apiClient';

interface Member {
  id: number;
  vorname: string;
  name: string;
  email: string;
  foto: string | null;
  fotoUpdated: string | null;
  eintritt: string | null;
  membershipYears: number;
  roles: string[];
  active: boolean;
}

interface Structure {
  board: Member[];
  members: Member[];
  guests: Member[];
  trainers: Member[];
  auditors: Member[];
  deactivated: Member[];
}

interface Stats {
  total: number;
  board: number;
  members: number;
  guests: number;
  trainers: number;
  auditors: number;
  deactivated: number;
}

const AdminStructure: React.FC = () => {
  const { user } = useContext(AuthContext);
  const [loading, setLoading] = useState(true);
  const [structure, setStructure] = useState<Structure | null>(null);
  const [stats, setStats] = useState<Stats | null>(null);
  const [activeTab, setActiveTab] = useState<'structure' | 'anniversary'>('structure');
  const [profileImages, setProfileImages] = useState<Record<number, string>>({});
  const [imageErrors, setImageErrors] = useState<Record<number, boolean>>({});
  const [expandedSections, setExpandedSections] = useState<Record<string, boolean>>({
    board: true,
    trainers: true,
    auditors: true,
    members: true,
    guests: false,
    deactivated: false,
  });

  const isBoardMember = user?.roles?.some((r: any) => r.name === 'board');
  const BASE_URL = localStorage.getItem('club_url') || 'http://localhost:4000';

  useEffect(() => {
    if (isBoardMember) fetchStructure();
  }, [isBoardMember]);

  const fetchProfileImage = (memberId: number, fotoUpdated?: string | null) => {
    const token = localStorage.getItem('jwt_token');
    if (!token) return;
    const timestamp = fotoUpdated || new Date().getTime();
    const imageUrl = `${BASE_URL}/api/members/user-image-url/${memberId}?size=S&token=${token}&t=${timestamp}`;
    setProfileImages(prev => ({ ...prev, [memberId]: imageUrl }));
  };

  const fetchStructure = async () => {
    setLoading(true);
    try {
      const response = await apiClient.get('/api/member-structure');
      if (response.data.success) {
        setStructure(response.data.structure);
        setStats(response.data.stats);
        const allMembers = [
          ...response.data.structure.board,
          ...response.data.structure.members,
          ...response.data.structure.trainers,
          ...response.data.structure.auditors,
          ...response.data.structure.guests,
          ...response.data.structure.deactivated,
        ];
        allMembers.forEach((member: Member) => {
          if (member.foto) fetchProfileImage(member.id, member.fotoUpdated);
        });
      }
    } catch (err) {
      console.error('Fehler beim Laden:', err);
    } finally {
      setLoading(false);
    }
  };

  const allActiveMembers = useMemo(() => {
    if (!structure) return [];
    const allMembers = [
      ...structure.board,
      ...structure.members,
      ...structure.trainers,
      ...structure.auditors,
    ];
    const uniqueMembers = allMembers.filter((member, index, self) =>
      index === self.findIndex((m) => m.id === member.id)
    );
    return uniqueMembers.sort((a, b) => b.membershipYears - a.membershipYears);
  }, [structure]);

  const anniversaryStats = useMemo(() => {
    if (allActiveMembers.length === 0) return null;
    const years = allActiveMembers.map(m => m.membershipYears);
    const totalYears = years.reduce((sum, y) => sum + y, 0);
    const avgYears = totalYears / years.length;
    const maxYears = Math.max(...years);
    const minYears = Math.min(...years);
    const milestones = {
      '25+': allActiveMembers.filter(m => m.membershipYears >= 25).length,
      '20-24': allActiveMembers.filter(m => m.membershipYears >= 20 && m.membershipYears < 25).length,
      '15-19': allActiveMembers.filter(m => m.membershipYears >= 15 && m.membershipYears < 20).length,
      '10-14': allActiveMembers.filter(m => m.membershipYears >= 10 && m.membershipYears < 15).length,
      '5-9': allActiveMembers.filter(m => m.membershipYears >= 5 && m.membershipYears < 10).length,
      '1-4': allActiveMembers.filter(m => m.membershipYears >= 1 && m.membershipYears < 5).length,
      '<1': allActiveMembers.filter(m => m.membershipYears < 1).length,
    };
    const upcomingAnniversaries = allActiveMembers.filter(m => {
      const nextMilestone = [5, 10, 15, 20, 25, 30, 35, 40].find(y => y > m.membershipYears);
      if (!nextMilestone) return false;
      return nextMilestone - m.membershipYears === 1;
    });
    return { totalYears, avgYears: Math.round(avgYears * 10) / 10, maxYears, minYears, milestones, upcomingAnniversaries };
  }, [allActiveMembers]);

  const toggleSection = (section: string) => {
    setExpandedSections(prev => ({ ...prev, [section]: !prev[section] }));
  };

  const formatDate = (dateStr: string | null) => {
    if (!dateStr) return '-';
    return new Date(dateStr).toLocaleDateString('de-DE');
  };

  const getCategoryIcon = (category: string) => {
    switch (category) {
      case 'board': return <ShieldCheckIcon className="h-5 w-5" />;
      case 'trainers': return <AcademicCapIcon className="h-5 w-5" />;
      case 'auditors': return <ShieldCheckIcon className="h-5 w-5" />;
      case 'members': return <UserGroupIcon className="h-5 w-5" />;
      case 'guests': return <UserIcon className="h-5 w-5" />;
      case 'deactivated': return <UserMinusIcon className="h-5 w-5" />;
      default: return <UserIcon className="h-5 w-5" />;
    }
  };

  const getCategoryToneStyle = (category: string) => {
    switch (category) {
      case 'board': return { bg: 'rgba(245,158,11,0.10)', border: 'rgba(245,158,11,0.30)', text: '#fcd34d' };
      case 'trainers': return { bg: 'rgba(59,130,246,0.10)', border: 'rgba(59,130,246,0.30)', text: '#93c5fd' };
      case 'auditors': return { bg: 'rgba(245,158,11,0.10)', border: 'rgba(245,158,11,0.30)', text: '#fbbf24' };
      case 'members': return { bg: 'rgba(16,185,129,0.10)', border: 'rgba(16,185,129,0.30)', text: '#86efac' };
      case 'guests': return { bg: 'rgba(255,255,255,0.04)', border: 'rgba(255,255,255,0.10)', text: '#cbd5e1' };
      case 'deactivated': return { bg: 'rgba(244,63,94,0.10)', border: 'rgba(244,63,94,0.30)', text: '#fda4af' };
      default: return { bg: 'rgba(255,255,255,0.04)', border: 'rgba(255,255,255,0.10)', text: '#cbd5e1' };
    }
  };

  const getCategoryLabel = (category: string) => {
    switch (category) {
      case 'board': return 'Vorstand';
      case 'trainers': return 'Trainer';
      case 'auditors': return 'Kassenprüfer';
      case 'members': return 'Mitglieder';
      case 'guests': return 'Gäste';
      case 'deactivated': return 'Deaktiviert';
      default: return category;
    }
  };

  const getMedalColor = (rank: number) => {
    if (rank === 1) return 'text-amber-600 dark:text-amber-400';
    if (rank === 2) return 'text-gray-700 dark:text-slate-300';
    if (rank === 3) return 'text-amber-700 dark:text-amber-600';
    return 'text-gray-500 dark:text-slate-500';
  };

  const getMilestoneTone = (years: number) => {
    if (years >= 25) return { bg: 'rgba(245,158,11,0.10)', border: 'rgba(245,158,11,0.30)' };
    if (years >= 20) return { bg: 'rgba(245,158,11,0.06)', border: 'rgba(245,158,11,0.25)' };
    if (years >= 15) return { bg: 'rgba(59,130,246,0.10)', border: 'rgba(59,130,246,0.30)' };
    if (years >= 10) return { bg: 'rgba(16,185,129,0.10)', border: 'rgba(16,185,129,0.30)' };
    if (years >= 5) return { bg: 'rgba(20,184,166,0.10)', border: 'rgba(20,184,166,0.30)' };
    return { bg: 'rgba(255,255,255,0.03)', border: 'rgba(255,255,255,0.08)' };
  };

  const cardClass = 'bg-white dark:bg-[#1c2432] border border-gray-200 dark:border-slate-700';

  if (!isBoardMember) {
    return (
      <div className="min-h-screen flex items-center justify-center p-4" style={{ background: 'transparent' }}>
        <div className={`rounded-3xl p-8 max-w-md text-center ${cardClass}`}>
          <ShieldCheckIcon className="h-16 w-16 text-rose-600 dark:text-rose-400 mx-auto mb-4" />
          <h2 className="text-xl font-black text-gray-900 dark:text-white mb-2">Zugriff verweigert</h2>
          <p className="text-gray-600 dark:text-slate-400">Diese Seite ist nur für Vorstandsmitglieder zugänglich.</p>
        </div>
      </div>
    );
  }

  const renderMemberCard = (member: Member, showRank?: number) => {
    const hasImage = member.foto && profileImages[member.id] && !imageErrors[member.id];
    const tone = getMilestoneTone(member.membershipYears);

    return (
      <div
        key={member.id}
        className="flex items-center gap-3 p-3 rounded-xl transition"
        style={{ background: tone.bg, border: `1px solid ${tone.border}` }}
      >
        {showRank && showRank <= 3 && (
          <div className="flex-shrink-0">
            <TrophyIcon className={`h-6 w-6 ${getMedalColor(showRank)}`} />
          </div>
        )}
        {showRank && showRank > 3 && (
          <div className="flex-shrink-0 w-6 text-center">
            <span className="text-sm font-bold text-gray-500 dark:text-slate-500 tabular-nums">{showRank}</span>
          </div>
        )}

        <div className="flex-shrink-0">
          {hasImage ? (
            <img
              src={profileImages[member.id]}
              alt={`${member.vorname} ${member.name}`}
              className="w-12 h-12 rounded-full object-cover border-2 border-gray-200 dark:border-slate-700"
              onError={() => setImageErrors(prev => ({ ...prev, [member.id]: true }))}
            />
          ) : (
            <div className="w-12 h-12 rounded-full flex items-center justify-center bg-gray-50 dark:bg-slate-900/60 border-2 border-gray-200 dark:border-slate-700">
              <UserIcon className="h-6 w-6 text-gray-500 dark:text-slate-500" />
            </div>
          )}
        </div>

        <div className="flex-1 min-w-0">
          <p className="font-bold text-gray-900 dark:text-white truncate">{member.vorname} {member.name}</p>
          <p className="text-sm text-gray-600 dark:text-slate-400">Mitglied seit {formatDate(member.eintritt)}</p>
        </div>

        <div className="flex-shrink-0 text-right">
          <p className="text-xl font-black text-amber-600 dark:text-amber-400 tabular-nums">{member.membershipYears}</p>
          <p className="text-xs text-gray-600 dark:text-slate-400">{member.membershipYears === 1 ? 'Jahr' : 'Jahre'}</p>
        </div>
      </div>
    );
  };

  const renderCategory = (category: string, members: Member[]) => {
    const isExpanded = expandedSections[category];
    const count = members.length;
    const tone = getCategoryToneStyle(category);

    return (
      <div key={category} className="mb-4">
        <button
          onClick={() => toggleSection(category)}
          className="w-full flex items-center gap-3 p-4 rounded-2xl transition-colors"
          style={{ background: tone.bg, border: `1px solid ${tone.border}`, color: tone.text }}
        >
          <div className="flex-shrink-0">
            {isExpanded ? <ChevronDownIcon className="h-5 w-5" /> : <ChevronRightIcon className="h-5 w-5" />}
          </div>
          <div className="flex-shrink-0">{getCategoryIcon(category)}</div>
          <span className="font-bold flex-1 text-left">{getCategoryLabel(category)}</span>
          <span className="px-3 py-1 rounded-full font-black tabular-nums" style={{ background: 'rgba(2,6,23,0.4)', color: tone.text }}>
            {count}
          </span>
        </button>

        {isExpanded && members.length > 0 && (
          <div className="mt-2 ml-4 pl-4 space-y-2 border-l-2 border-gray-200 dark:border-slate-700">
            {members.map(m => renderMemberCard(m))}
          </div>
        )}

        {isExpanded && members.length === 0 && (
          <div className="mt-2 ml-4 pl-4 border-l-2 border-gray-200 dark:border-slate-700">
            <p className="text-sm text-gray-500 dark:text-slate-500 italic py-2">Keine Mitglieder in dieser Kategorie</p>
          </div>
        )}
      </div>
    );
  };

  const tabBtn = (active: boolean) =>
    `inline-flex items-center gap-2 px-4 py-2.5 rounded-xl text-sm font-bold transition ${
      active ? 'bg-amber-500 text-slate-950' : 'text-gray-600 dark:text-slate-400 hover:text-gray-900 dark:hover:text-white'
    }`;

  return (
    <div className="min-h-screen font-sans text-gray-900 dark:text-slate-200" style={{ background: 'transparent', WebkitFontSmoothing: 'antialiased' }}>
      <div className="max-w-7xl mx-auto py-2 sm:py-4">
        {/* Hero */}
        <section className="pt-2 pb-6 px-4 sm:px-6">
          <div className="grid lg:grid-cols-12 gap-6 lg:gap-8 items-end">
            <div className="lg:col-span-8">
              <div className="text-[11px] uppercase tracking-[0.22em] text-amber-600/80 dark:text-amber-400/80 font-semibold mb-3">
                ADMIN · Vereinsstruktur
              </div>
              <h1 className="text-4xl sm:text-5xl md:text-6xl font-black text-gray-900 dark:text-white leading-[1.05] tracking-tight" style={{ letterSpacing: '-0.04em' }}>
                Wer was<br /><span className="text-amber-600 dark:text-amber-400">kann.</span>
              </h1>
              <p className="text-gray-700 dark:text-slate-300 text-base md:text-lg leading-relaxed max-w-2xl mt-4">
                {stats ? (
                  <>
                    <span className="font-bold text-gray-900 dark:text-white">{stats.total} aktive Mitglieder</span> ·{' '}
                    <span className="font-bold text-amber-700 dark:text-amber-300">{stats.board} Vorstand</span> ·{' '}
                    <span className="font-bold text-blue-700 dark:text-blue-300">{stats.trainers} Trainer</span> ·{' '}
                    <span className="font-bold text-emerald-700 dark:text-emerald-300">{stats.members} reguläre</span>
                    {stats.deactivated > 0 && <> · <span className="font-bold text-rose-700 dark:text-rose-300">{stats.deactivated} deaktiviert</span></>}.
                  </>
                ) : (
                  'Wer ist Vorstand, wer trainiert, wer prüft die Kasse — alle Rollen auf einen Blick.'
                )}
              </p>
            </div>
            {stats && (
              <div className="lg:col-span-4 grid grid-cols-3 gap-3">
                <div className={`rounded-2xl p-3 lg:p-4 ${cardClass}`}>
                  <div className="text-[10px] uppercase tracking-widest text-gray-500 dark:text-slate-500">Gesamt</div>
                  <div className="text-2xl lg:text-3xl font-black mt-1 tabular-nums text-gray-900 dark:text-white">{stats.total}</div>
                  <div className="text-[10px] text-gray-600 dark:text-slate-400 mt-0.5">aktiv</div>
                </div>
                <div className={`rounded-2xl p-3 lg:p-4 ${cardClass}`}>
                  <div className="text-[10px] uppercase tracking-widest text-gray-500 dark:text-slate-500">Vorstand</div>
                  <div className="text-2xl lg:text-3xl font-black mt-1 tabular-nums text-amber-600 dark:text-amber-400">{stats.board}</div>
                  <div className="text-[10px] text-gray-600 dark:text-slate-400 mt-0.5">Köpfe</div>
                </div>
                <div className={`rounded-2xl p-3 lg:p-4 ${cardClass}`}>
                  <div className="text-[10px] uppercase tracking-widest text-gray-500 dark:text-slate-500">Gäste</div>
                  <div className="text-2xl lg:text-3xl font-black mt-1 tabular-nums text-gray-900 dark:text-white">{stats.guests}</div>
                  <div className="text-[10px] text-gray-600 dark:text-slate-400 mt-0.5">Karten</div>
                </div>
              </div>
            )}
          </div>
        </section>

        <div className="px-4 sm:px-6">
          {/* Tabs */}
          <div className="mb-6">
            <div className="inline-flex p-1 rounded-2xl bg-gray-100 dark:bg-white/[0.04] border border-gray-200 dark:border-white/10">
              <button onClick={() => setActiveTab('structure')} className={tabBtn(activeTab === 'structure')}>
                <BuildingOfficeIcon className="h-5 w-5" />
                Struktur
              </button>
              <button onClick={() => setActiveTab('anniversary')} className={tabBtn(activeTab === 'anniversary')}>
                <TrophyIcon className="h-5 w-5" />
                Jubiläum
              </button>
            </div>
          </div>

          {loading && (
            <div className="flex items-center justify-center py-12">
              <div className="animate-spin h-8 w-8 border-4 border-amber-500 border-t-transparent rounded-full" />
            </div>
          )}

          {/* Structure Tab */}
          {!loading && activeTab === 'structure' && structure && (
            <div className="space-y-2">
              {renderCategory('board', structure.board)}
              {renderCategory('trainers', structure.trainers)}
              {renderCategory('auditors', structure.auditors)}
              {renderCategory('members', structure.members)}
              {renderCategory('guests', structure.guests)}
              {renderCategory('deactivated', structure.deactivated)}
            </div>
          )}

          {/* Anniversary Tab */}
          {!loading && activeTab === 'anniversary' && anniversaryStats && (
            <>
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 mb-6">
                <div className={`rounded-2xl p-5 text-center ${cardClass}`}>
                  <p className="text-3xl font-black text-amber-600 dark:text-amber-400 tabular-nums">{anniversaryStats.totalYears}</p>
                  <p className="text-sm text-gray-600 dark:text-slate-400">Jahre gesamt</p>
                </div>
                <div className={`rounded-2xl p-5 text-center ${cardClass}`}>
                  <p className="text-3xl font-black text-blue-700 dark:text-blue-300 tabular-nums">{anniversaryStats.avgYears}</p>
                  <p className="text-sm text-gray-600 dark:text-slate-400">Ø Jahre</p>
                </div>
                <div className="rounded-2xl p-5 text-center" style={{ background: 'rgba(245,158,11,0.10)', border: '1px solid rgba(245,158,11,0.30)' }}>
                  <p className="text-3xl font-black text-amber-700 dark:text-amber-300 tabular-nums">{anniversaryStats.maxYears}</p>
                  <p className="text-sm text-amber-700/80 dark:text-amber-300/80">Längstes</p>
                </div>
                <div className={`rounded-2xl p-5 text-center ${cardClass}`}>
                  <p className="text-3xl font-black text-gray-900 dark:text-white tabular-nums">{allActiveMembers.length}</p>
                  <p className="text-sm text-gray-600 dark:text-slate-400">Mitglieder</p>
                </div>
              </div>

              <div className={`rounded-2xl p-5 mb-6 ${cardClass}`}>
                <h3 className="font-bold text-gray-900 dark:text-white mb-4 flex items-center gap-2">
                  <StarIcon className="h-5 w-5 text-amber-600 dark:text-amber-400" />
                  Mitgliedschaftsdauer Verteilung
                </h3>
                <div className="space-y-3">
                  {Object.entries(anniversaryStats.milestones).map(([range, count]) => {
                    const percentage = allActiveMembers.length > 0 ? (count / allActiveMembers.length) * 100 : 0;
                    const colors: Record<string, string> = {
                      '25+': 'rgba(245,158,11,0.5)',
                      '20-24': 'rgba(245,158,11,0.4)',
                      '15-19': 'rgba(59,130,246,0.5)',
                      '10-14': 'rgba(16,185,129,0.5)',
                      '5-9': 'rgba(20,184,166,0.5)',
                      '1-4': 'rgba(148,163,184,0.5)',
                      '<1': 'rgba(148,163,184,0.3)',
                    };
                    return (
                      <div key={range} className="flex items-center gap-3">
                        <span className="w-16 text-sm font-bold text-gray-700 dark:text-slate-300 tabular-nums">{range} J.</span>
                        <div className="flex-1 h-6 rounded-full overflow-hidden relative bg-gray-100 dark:bg-white/[0.04]">
                          <div
                            className="h-full rounded-full transition-all duration-500"
                            style={{ width: `${percentage}%`, background: colors[range] }}
                          />
                        </div>
                        <span className="w-12 text-sm font-black text-gray-900 dark:text-white text-right tabular-nums">{count}</span>
                      </div>
                    );
                  })}
                </div>
              </div>

              {anniversaryStats.upcomingAnniversaries.length > 0 && (
                <div className="rounded-2xl p-5 mb-6" style={{ background: 'rgba(245,158,11,0.10)', border: '1px solid rgba(245,158,11,0.30)' }}>
                  <h3 className="font-bold text-gray-900 dark:text-white mb-4 flex items-center gap-2">
                    <SparklesIcon className="h-5 w-5 text-amber-600 dark:text-amber-400" />
                    Jubiläen im nächsten Jahr
                  </h3>
                  <div className="space-y-2">
                    {anniversaryStats.upcomingAnniversaries.map(member => {
                      const nextMilestone = [5, 10, 15, 20, 25, 30, 35, 40].find(y => y > member.membershipYears);
                      return (
                        <div key={member.id} className="flex items-center gap-3 p-2 rounded-lg bg-white/60 dark:bg-slate-950/40">
                          <TrophyIcon className="h-5 w-5 text-amber-600 dark:text-amber-400" />
                          <span className="font-bold text-gray-900 dark:text-white">{member.vorname} {member.name}</span>
                          <span className="text-sm text-gray-600 dark:text-slate-400">→</span>
                          <span className="font-black text-amber-700 dark:text-amber-300 tabular-nums">{nextMilestone} Jahre</span>
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}

              <div className={`rounded-2xl overflow-hidden ${cardClass}`}>
                <div className="px-5 py-4 border-b border-gray-200 dark:border-slate-800">
                  <h3 className="font-bold text-gray-900 dark:text-white flex items-center gap-2">
                    <TrophyIcon className="h-5 w-5 text-amber-600 dark:text-amber-400" />
                    Rangliste nach Mitgliedschaft
                  </h3>
                </div>
                <div className="p-4 space-y-2 max-h-[600px] overflow-y-auto">
                  {allActiveMembers.map((member, index) => renderMemberCard(member, index + 1))}
                </div>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default AdminStructure;
